import { Box } from '@wix/design-system';
import React, { useMemo, useState } from 'react';
import { getRuleElementPropsFromPresentation } from '../../../../utils/get-rule-element-props-from-presentation';
import { wdsThemeColors } from '../../../../utils/wds-theme-colors';
import { ElementStatus } from '../../../rule-element/types';
import { useElementToggle } from '../../../rule-element/use-element-toggle/use-element-toggle';
import { RuleElementGroupedList } from '../../../rule-element-grouped-list/rule-element-grouped-list';
import { useSharedAltTextScreenContext } from '../context';
import { hasImageExtension } from '../utils';
import { AltTextImageItem } from './alt-text-image-item';
export const AltTextImageList = _ref => {
  let {
    showSummary = true,
    disabled = false,
    renderTopContent,
    renderInputSuffix,
    helperTip,
    bottomContent
  } = _ref;
  const {
    state,
    actions,
    translations,
    biCallbacks,
    design,
    wizardPresentation
  } = useSharedAltTextScreenContext();
  const labelSize = (design == null ? void 0 : design.labelSize) ?? 'tiny';
  const ruleElementProps = useMemo(() => wizardPresentation ? getRuleElementPropsFromPresentation(wizardPresentation) : {
    hoveredBorderColor: wdsThemeColors.borderFocus,
    iconButtonPriority: 'secondary',
    notHoveredBorderColor: wdsThemeColors.borderStandard,
    width: 'normal'
  }, [wizardPresentation]);
  const {
    images,
    groups,
    selectedImageId,
    isLoading,
    generatingImageIds,
    scrollerRef,
    showDecorativeOption
  } = state;
  const {
    onToggleImage,
    onUpdateAltText,
    onUpdateDecorative,
    setIsLoading
  } = actions;
  const {
    toggleElement
  } = useElementToggle();
  const openNextElement = () => {}; // No-op to disable auto-advance

  const [, setShowLoader] = useState(false);
  const [enlargedImageId, setEnlargedImageId] = useState(null);
  const getFormValidation = (status, altText) => {
    if (altText && hasImageExtension(altText) && translations.imageItem.imageExtensionErrorMessage) {
      return {
        message: translations.imageItem.imageExtensionErrorMessage,
        status: 'error'
      };
    }
    if (status === ElementStatus.Warning) {
      return {
        message: translations.imageItem.failedToGenerateWarning,
        status: 'warning'
      };
    }
    return null;
  };
  const buildElement = image => {
    const isOpen = !disabled && selectedImageId === image.id;
    const isImageEnlarged = enlargedImageId === image.id;
    const isUnsupportedFormat = image.isAiSupported === false;
    const isImageGenerating = !isUnsupportedFormat && (image.status === ElementStatus.Loading || (generatingImageIds ?? new Set()).has(image.id));
    const handleEnlargeClick = () => {
      setEnlargedImageId(image.id);
      biCallbacks.element.onEnlargeClick(image.id);
    };
    const handleMinimize = () => {
      setEnlargedImageId(null);
      biCallbacks.element.onMinimizeClick(image.id);
    };
    const formValidation = getFormValidation(image.status, image.altText) ?? undefined;
    const prefixStatusIconTooltip = image.status === ElementStatus.Warning ? translations.imageItem.failedToGenerateWarning : undefined;
    return {
      contentComponent: /*#__PURE__*/React.createElement(AltTextImageItem, {
        altText: image.altText ?? '',
        formValidation: formValidation,
        helperTip: helperTip,
        hoveredBorderColor: ruleElementProps.hoveredBorderColor,
        iconButtonPriority: ruleElementProps.iconButtonPriority,
        iconButtonSkin: ruleElementProps.iconButtonSkin,
        id: image.id,
        imageUrl: image.url,
        inputSuffix: renderInputSuffix == null ? void 0 : renderInputSuffix(image),
        isDecorative: image.isDecorative,
        isDisabled: disabled || isLoading,
        isImageEnlarged: isImageEnlarged,
        isLinked: image.isLinked,
        isLoading: isImageGenerating,
        isOpen: isOpen,
        key: image.id,
        labelSize: labelSize,
        notHoveredBorderColor: ruleElementProps.notHoveredBorderColor,
        prefixStatusIconTooltip: prefixStatusIconTooltip,
        showDecorativeOption: showDecorativeOption,
        showEnlargeButton: !!renderTopContent,
        status: image.status,
        topContent: renderTopContent == null ? void 0 : renderTopContent(image, isImageEnlarged, handleMinimize),
        translations: translations.imageItem,
        width: ruleElementProps.width,
        onBlur: () => biCallbacks.element.onBlur(image.id, image.altText ?? ''),
        onClick: () => {
          const willOpen = !isOpen;
          if (isOpen) {
            setEnlargedImageId(null);
          }
          toggleElement(image.id, () => actions.onOpenImage(image))();
          biCallbacks.onImageToggle(image.id, willOpen);
          onToggleImage(image.id);
        },
        onDecorativeChange: isDecorative => {
          biCallbacks.onDecorativeToggle(image.id, isDecorative);
          onUpdateDecorative(image.id, isDecorative);
        },
        onEnlargeClick: handleEnlargeClick,
        onShowPrefixIconTooltip: biCallbacks.element.onPrefixIconTooltipView,
        onStatusChange: () => biCallbacks.element.onStatusChange(image.id),
        onValueChange: value => onUpdateAltText(image.id, value)
      }),
      id: image.id,
      status: image.status
    };
  };
  const propGroups = useMemo(() => {
    if (groups && groups.length > 0) {
      return groups.map(group => ({
        elements: group.images.map(buildElement),
        id: group.id,
        name: group.label ?? group.id
      }));
    }
    return [{
      elements: images.map(buildElement),
      id: 'default',
      name: ''
    }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biCallbacks, groups, images, selectedImageId, enlargedImageId, disabled, isLoading, generatingImageIds, translations.imageItem, toggleElement, onToggleImage, onUpdateAltText, onUpdateDecorative, actions, renderTopContent, renderInputSuffix, helperTip, showDecorativeOption, labelSize, ruleElementProps]);
  const handleOpenImageElement = element => {
    const image = images.find(img => img.id === element.id);
    if (image) {
      actions.onOpenImage(image);
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    dataHook: "alt-text-image-list-container",
    display: "block",
    overflow: "auto",
    padding: "SP2 SP6 SP2 SP6",
    ref: scrollerRef
  }, /*#__PURE__*/React.createElement(RuleElementGroupedList, {
    groups: propGroups,
    headerTitle: translations.listHeaderTitle,
    openImageElement: handleOpenImageElement,
    openNextElement: openNextElement,
    setIsLoading: setIsLoading,
    setShowLoader: setShowLoader,
    showSummary: showSummary
  }), bottomContent);
};
//# sourceMappingURL=alt-text-image-list.js.map